import { transformIncomingOfficeDesk } from '@/utils/dataTransformers'
import { defineStore } from 'pinia'
import { useOfficeDesksStore } from '@/stores/officeDesks/store'
import * as Api from './api'
import { callWithMeta, createMeta, waitFor, type Meta } from '@/utils/store'
import { useDevicesStore } from '@/stores/devices/store'
import { useDeviceStore } from '@/stores/device/store'
import type { ColorName } from '@/utils/colors'

export type OfficeDesk = {
  id: number
  callLogPermission: boolean
  contactsPermission: boolean
  devices?: number[]
  email: string
  extensions: number[]
  faxPermission: boolean
  name: string
  password: string
  smsPermission: boolean
  username: string
  emailStatus: string
  color?: ColorName | null
}

type State = {
  officeDeskData?: OfficeDesk
  meta: Meta<{
    officeDeskData: { id: string | number } | undefined
    load: { id: string | number }
    create: undefined
    update: { id: string | number }
    delete: { id: string | number }
  }>
}

const getDefaultState = (): State => ({
  officeDeskData: undefined,
  meta: createMeta(['officeDeskData', 'load', 'create', 'update', 'delete']),
})

export const useOfficeDeskStore = defineStore('officeDesk', {
  state: getDefaultState,

  getters: {
    get: (state) => state.officeDeskData,
  },

  actions: {
    RESET() {
      this.officeDeskData = getDefaultState().officeDeskData
      this.meta.officeDeskData = getDefaultState().meta.officeDeskData
    },

    // eslint-disable-next-line
    async load(id: number | string, { useCache = true } = {}) {
      // TODO: Implement singular GET route if necessary
      // const officeDeskData = await dispatch(
      //   "network/loadOfficeDesk",
      //   officeDeskId,
      //   {
      //     root: true,
      //   }
      // )

      const hasCached =
        ['pending', 'success'].includes(this.meta.officeDeskData.status) &&
        Number(id) === Number(this.meta.officeDeskData.params?.id)

      if (useCache && hasCached) {
        await waitFor(() => this.meta.officeDeskData.status !== 'pending')

        return
      }

      await callWithMeta({
        store: this,
        metaFields: { officeDeskData: { id }, load: { id } },
        apiCall: async () => Api.loadOfficeDesk(id),
        onSuccess: ({ result, isLatest }) => {
          if (isLatest('officeDeskData')) {
            this.officeDeskData = transformIncomingOfficeDesk(result.payload)
          }
        },
      })
    },

    async reload() {
      if (
        this.meta.officeDeskData.status !== 'initial' &&
        this.meta.officeDeskData.params
      ) {
        await this.load(this.meta.officeDeskData.params.id, { useCache: false })
      }
    },

    async create(officeDeskData: any) {
      const { result } = await callWithMeta({
        store: this,
        metaFields: { officeDeskData: undefined, create: undefined },
        apiCall: async () => await Api.createOfficeDesk(officeDeskData),
        onSuccess: ({ isLatest }) => {
          if (isLatest('create')) {
            useDevicesStore().load({ useCache: false })

            useDeviceStore().reload()

            setTimeout(async () => {
              await useOfficeDesksStore().load({ useCache: false })

              this.reload()
            })
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },

    async update({ id, data }: any) {
      const { result } = await callWithMeta({
        store: this,
        metaFields: { officeDeskData: { id }, update: { id } },
        apiCall: async () => await Api.updateOfficeDesk(id, data),
        onSuccess: ({ isLatest }) => {
          if (isLatest('update')) {
            useDevicesStore().load({ useCache: false })

            useDeviceStore().reload()

            setTimeout(async () => {
              await useOfficeDesksStore().load({ useCache: false })

              this.reload()
            })
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },

    async delete(id: string) {
      const params = { id }

      const { result } = await callWithMeta({
        store: this,
        metaFields: { officeDeskData: params, delete: params },
        apiCall: async () => await Api.deleteOfficeDesk(id),
        onSuccess: ({ isLatest }) => {
          if (isLatest('delete')) {
            useDevicesStore().load({ useCache: false })

            useDeviceStore().reload()

            useOfficeDesksStore().load({ useCache: false })
          }

          if (isLatest('officeDeskData')) {
            this.RESET()
          }
        },
      })

      if (result.status === 'error') {
        throw result.payload
      }
    },
  },
})
